import React, {useState, useEffect} from 'react'
import { Line, Bar } from "react-chartjs-2";
import { FinancialChartLayout } from "../layout/financial/FinancialChartLayout";
import {DateButtonGroup} from "../filters/DateButtonGroup";
import {getOrderReportsApi} from "../../../../api/admin/reports";
import {getProductGroups as getProductGroupsApi} from "../../../../api/admin/reports";
import moment from 'moment'
import {
  COLORS_PRODUCT_GROUP_TYPES,
  TRANSLATED_PRODUCT_GROUP_TYPES
} from "../../../../config/constants/ProductGroups";
import { connect } from "react-redux";
import { FormattedMessage, useIntl } from 'react-intl';

const groupsWithMetric = (dataset, productGroups, format) => productGroups.filter(productGroup => (
  dataset?.quantity?.[productGroup.name]?.[format]
));

const formatMetric = value => Number(value || 0).toFixed(2);

const QualitativeLineChart = (props) => {
  const { title, subtitle, options, id, className, expandButton, expandClassName} = props;
  const [dateFilter, setDateFilter] = useState({startDate: moment().subtract(6,'d'), endDate: moment()})
  const [productFilter, setProductFilter] = useState(null)
  const [chartData, setChartData] = useState({})
  const [productGroups, setProductGroups] = useState([])
  const [selectOptions, setSelectOptions] = useState([])
  const [chartType, setChartType] = useState('Line')
  const intl = useIntl();

  useEffect(() => {
    getProductGroups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    productGroupSelectFields()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productGroups])

  useEffect(() => {
    getOrderReports()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFilter, dateFilter])

  const setDateFilters = (date) => {
    setDateFilter(date)
  }

  const setProductFilters = (product) => {
    setProductFilter(product)
  }

  const createChartData = () => {
    switch (chartData.dataset.format) {

      case 'byDay':
        let handleDataByDay = []
        let days = []
        let firstDay = dateFilter.startDate
        let lastDay = dateFilter.endDate

        while (moment(firstDay, "MM/DD/YYYY").valueOf() <= moment(lastDay, "DD/MM/YYYY").valueOf()) {
          days.push(moment(firstDay, "MM/DD/YYYY").format("DD/MM/YYYY"));
          firstDay = moment(firstDay, "MM/DD/YYYY").add(1, "days").format("MM/DD/YYYY");
        }

        switch (chartData.dataset.aggBy) {
          case 'productGroupId':
            const dayGroups = groupsWithMetric(chartData.dataset, productGroups, 'byDay');
            dayGroups.forEach(productGroup => {
              handleDataByDay[productGroup.name] = [];

              days.forEach(day => {
                handleDataByDay[productGroup.name].push(formatMetric(
                  chartData.dataset.quantity[productGroup.name].byDay[moment(day, "DD/MM/YYYY").format("YYYY-MM-DD")]
                ));
              });
            });

            let datasets = [];

            dayGroups.forEach(productGroup => {
              const translatedGroup = TRANSLATED_PRODUCT_GROUP_TYPES[productGroup.name];
              const labelText = translatedGroup?.props?.id
                ? intl.formatMessage({ id: translatedGroup.props.id })
                : productGroup.name;

              datasets.push({
                label: `${labelText}: `,
                borderColor: COLORS_PRODUCT_GROUP_TYPES[productGroup.name],
                backgroundColor: COLORS_PRODUCT_GROUP_TYPES[productGroup.name],
                data: handleDataByDay[productGroup.name],
                fill: false,
              });
            });

            return {
              labels: days,
              datasets: datasets,
            }

          default:
            days.forEach(day => {
              handleDataByDay.push(formatMetric(
                chartData.dataset.quantity?.byDay?.[moment(day, "DD/MM/YYYY").format("YYYY-MM-DD")]
              ))
            });

            return {
              labels: days,
              datasets: [
                {
                  data: handleDataByDay
                }
              ]
            }
          }

      case 'byWeek':
        let handleDataByWeek = []
        let weeks = []

        switch (chartData.dataset.aggBy) {
          case 'productGroupId':
            const weekGroups = groupsWithMetric(chartData.dataset, productGroups, 'byWeek');
            const weekKeys = [...new Set(weekGroups.flatMap(productGroup => (
              Object.keys(chartData.dataset.quantity[productGroup.name].byWeek)
            )))].sort();
            weekKeys.forEach(week => {
              let weekRange = moment(week).format("DD/MM/YYYY") + ' - ' + moment(week).add(1, "weeks").format("DD/MM/YYYY")
              weeks.push(weekRange)
            })

            weekGroups.forEach(productGroup => {
              handleDataByWeek[productGroup.name] = [];

              weekKeys.forEach(week => {
                handleDataByWeek[productGroup.name].push(formatMetric(
                  chartData.dataset.quantity[productGroup.name].byWeek[week]
                ))
              })
            });

            let datasets = [];

            weekGroups.forEach(productGroup => {
              const translatedGroup = TRANSLATED_PRODUCT_GROUP_TYPES[productGroup.name];
              const labelText = translatedGroup?.props?.id
                ? intl.formatMessage({ id: translatedGroup.props.id })
                : productGroup.name;

              datasets.push({
                label: `${labelText}: `,
                borderColor: COLORS_PRODUCT_GROUP_TYPES[productGroup.name],
                backgroundColor: COLORS_PRODUCT_GROUP_TYPES[productGroup.name],
                data: handleDataByWeek[productGroup.name],
                fill: false,
              });
            });

            return {
              labels: weeks,
              datasets: datasets,
            };

          default:
            Object.keys(chartData.dataset.quantity?.byWeek || {}).forEach(week => {
              let weekRange = moment(week).format("DD/MM/YYYY") + ' - ' + moment(week).add(1, "weeks").format("DD/MM/YYYY")
              weeks.push(weekRange)
            })

            Object.values(chartData.dataset.quantity?.byWeek || {}).forEach(number => {
              handleDataByWeek.push(formatMetric(number))
            })

            return {
              labels: weeks,
              datasets: [
                {
                  data: handleDataByWeek
                }
              ]
            }
        }

      case 'byMonth':
        let handleDataByMonth = []
        let months = []
        let firstMonth = dateFilter.startDate
        let endMonth = dateFilter.endDate

        while (moment(firstMonth, "MM/DD/YYYY").valueOf() <= moment(endMonth, "DD/MM/YYYY").valueOf()) {
          months.push(moment(firstMonth, "MM/DD/YYYY").format("MM/YYYY"));
          firstMonth = moment(firstMonth, "MM/DD/YYYY").add(1, "months").format("MM/DD/YYYY");
        }

        switch (chartData.dataset.aggBy) {
          case 'productGroupId':
            const monthGroups = groupsWithMetric(chartData.dataset, productGroups, 'byMonth');
            monthGroups.forEach(productGroup => {
              handleDataByMonth[productGroup.name] = [];

              months.forEach(month => {
                handleDataByMonth[productGroup.name].push(formatMetric(
                  chartData.dataset.quantity[productGroup.name].byMonth[moment(month, "MM/YYYY").format("YYYY-MM")]
                ));
              });
            });

            let datasets = [];

            monthGroups.forEach(productGroup => {
              const translatedGroup = TRANSLATED_PRODUCT_GROUP_TYPES[productGroup.name];
              const labelText = translatedGroup?.props?.id
                ? intl.formatMessage({ id: translatedGroup.props.id })
                : productGroup.name;

              datasets.push({
                label: `${labelText}: `,
                borderColor: COLORS_PRODUCT_GROUP_TYPES[productGroup.name],
                backgroundColor: COLORS_PRODUCT_GROUP_TYPES[productGroup.name],
                data: handleDataByMonth[productGroup.name],
                fill: false,
              });
            });

            return {
              labels: months,
              datasets: datasets,
            }

          default:
            months.forEach(month => {
              handleDataByMonth.push(formatMetric(
                chartData.dataset.quantity?.byMonth?.[moment(month, "MM/YYYY").format("YYYY-MM")]
              ))
            })

            return {
              labels: months,
              datasets: [
                {
                  data: handleDataByMonth
                }
              ]
            }
        }

      case 'byYear':
        let handleDataByYear = []
        let years = []
        let firstYear = dateFilter.startDate
        let endYear = dateFilter.endDate

        while (moment(firstYear, "MM/DD/YYYY").valueOf() <= moment(endYear, "DD/MM/YYYY").valueOf()) {
          years.push(moment(firstYear, "MM/DD/YYYY").format("YYYY"));
          firstYear = moment(firstYear, "MM/DD/YYYY").add(1, "years").format("MM/DD/YYYY");
        }

        switch (chartData.dataset.aggBy) {
          case 'productGroupId':
            const yearGroups = groupsWithMetric(chartData.dataset, productGroups, 'byYear');
            yearGroups.forEach(productGroup => {
              handleDataByYear[productGroup.name] = [];

              years.forEach(year => {
                handleDataByYear[productGroup.name].push(formatMetric(
                  chartData.dataset.quantity[productGroup.name].byYear[year]
                ));
              });
            });

            let datasets = [];

            yearGroups.forEach(productGroup => {
              const translatedGroup = TRANSLATED_PRODUCT_GROUP_TYPES[productGroup.name];
              const labelText = translatedGroup?.props?.id
                ? intl.formatMessage({ id: translatedGroup.props.id })
                : productGroup.name;

              datasets.push({
                label: `${labelText}: `,
                borderColor: COLORS_PRODUCT_GROUP_TYPES[productGroup.name],
                backgroundColor: COLORS_PRODUCT_GROUP_TYPES[productGroup.name],
                data: handleDataByYear[productGroup.name],
                fill: false,
              });
            });

            return {
              labels: years,
              datasets: datasets,
            }

          default:
            years.forEach(year => {
              handleDataByYear.push(formatMetric(chartData.dataset.quantity?.byYear?.[year]))
            })

            return {
              labels: years,
              datasets: [
                {
                  data: handleDataByYear
                }
              ]
            }
        }

      default: break;
    }
  }

  const getOrderReports = () => {
    getOrderReportsApi({
      fromDate: dateFilter.startDate ? moment(dateFilter.startDate).format("YYYY/MM/DD") : null,
      toDate: dateFilter.endDate ? moment(dateFilter.endDate).format("YYYY/MM/DD") : null,
      productGroupId: productFilter ? productFilter : null,
      aggBy: productFilter ? null : 'productGroupId',
    }).then((response) => {
      const responseData = response?.dataset ? response : response?.data;
      if (!responseData || !responseData.dataset || !responseData.dataset.quantity) {
        setChartData({});
        return;
      }

      setChartData(responseData);
    });
  };

  const getProductGroups = () => {

    getProductGroupsApi({
      pagination: {
        disablePagination: true
      }
    }).then((response) => {
      if (!response || !response.data) {
        return;
      }

      setProductGroups(response.data)
    });
  };

  const productGroupSelectFields = () => {
    let translationsList = TRANSLATED_PRODUCT_GROUP_TYPES;
    let options = [{'value': null, 'label': <FormattedMessage id="components.pages.reports.allProducts"/>}];

    productGroups.forEach(productGroup => {
      options.push(
        {
          'value': productGroup.id,
          'label': translationsList[productGroup.name]
        }
      )
    })
    setSelectOptions(options)
  }

  const renderChart = () => {
    let chartOptions = options;

    chartOptions.legend = {
      display: true,
      position: "bottom",
      labels: {
        usePointStyle: true,
        padding: 16,
      },
      reverse: true,
    };

    if (! chartData.dataset.aggBy) {
      chartOptions.legend.display = false;
    }

    if (chartType === 'bar') {
      return (
        <Bar
          data={createChartData}
          options={chartOptions}
          id={id}
          className={className}
        />
      )
    }
    return (
      <Line
        data={createChartData}
        options={chartOptions}
        id={id}
        className={className}
      />
    )
  }

  const setType = (type) => {
    setChartType(type)
  }

  const getRange = () => {
    switch (chartData?.dataset?.format) {
      case 'byWeek':
        return moment(dateFilter.startDate).startOf('isoWeek').format('DD/MM/YYYY') + " - " + moment(dateFilter.endDate).startOf('isoWeek').add(1, 'week').format('DD/MM/YYYY');
      case 'byDay':
      case 'byMonth':
      case 'byYear':
      default:
        return moment(dateFilter.startDate).format('DD/MM/YYYY') + " - " + moment(dateFilter.endDate).format('DD/MM/YYYY');
    }
  }

  const hasChartData = chartData?.dataset?.quantity
    && ['byDay', 'byWeek', 'byMonth', 'byYear'].includes(chartData.dataset.format);

  return (
    <FinancialChartLayout
      title={title}
      subtitle={subtitle}
      range={getRange()}
      filter={
        <DateButtonGroup
          isActiveClass="btn btn-default btn-date-range-filter active"
          isInactiveClass="btn btn-default btn-date-range-filter"
          dateFilter={setDateFilters}
          productFilter={setProductFilters}
          selectOptions={selectOptions}
          setType={setType}
          expandButton={expandButton}
          chartType={chartType}
        />
      }
      expandClassName={expandClassName}
    >
    { hasChartData &&
        renderChart()
    }
    </FinancialChartLayout>
  )
}

const QualitativeLine = connect(null, {})(QualitativeLineChart);

export { QualitativeLine, QualitativeLineChart }
